import { useResetMultipleStoreData } from 'features/request/useResetStoreData';
import { ContractChangeStatusEnum, ContractStatusEnum, ProjectStatusEnum } from 'gql/graphql';
import { useAccess } from 'hooks/useAccess';
import { useProjectData } from 'hooks/useProjectData';
import { useUerContractSeasons } from 'pages/Project/Common/Contract/hooks/useUerContractSeasons';
import { ProjectPageLayout } from 'pages/Project/Common/Layouts/ProjectPageLayout';
import { Tabs } from 'uikit';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

import { AgreementFormToolbar } from '../AgreementFormToolbar';
import { AgreementPriceListMtr } from '../AgreementPriceListMtr';
import { AgreementPriceListOther } from '../AgreementPriceListOther';
import { AgreementPriceListRatio } from '../AgreementPriceListRatio';
import { AgreementPriceListUer } from '../AgreementPriceListUer';
import { useAgreementItem } from '../hooks/useAgreementItem';

import { useAgreementForm } from './useAgreementForm';

const TabsStyle = { marginTop: '30px' };

export const AgreementForm = () => {
    const { id: projectId, agreementId: agreementIdString } = useParams();
    const agreementId = Number(agreementIdString);
    const { projectData, refreshProject } = useProjectData(projectId);
    const contractId = projectData?.contractId;
    const { additionalAgreement: additionalAgreementRules } = useAccess();

    const agreementTableApiRef = useRef<{ refreshCurrentTable: (skipCheckedReset?: boolean) => void }>();

    const isDisabledByContractStatus =
        projectData?.contractNavigation?.data?.contractNavData?.status === ContractStatusEnum.Delayed;
    const isProjectStatusDelayed = projectData?.status === ProjectStatusEnum.Delayed;
    const isProjectStatusCompleted = projectData?.status === ProjectStatusEnum.Completed;

    const isDisabledByProjectStatus = isProjectStatusDelayed || isProjectStatusCompleted;

    const [needUpdateCurrentPriceList, setNeedUpdateCurrentPriceList] = useState(false);

    const needUpdatePriceListHandler = useCallback((nextState: boolean) => {
        setNeedUpdateCurrentPriceList(nextState);
    }, []);

    const { agreementItemData, refreshData } = useAgreementItem({
        currentAgreementItemId: agreementId,
    });

    const { onChangeTab, getTabLabel, renderTabItem, currentTab, breadCrumbsItems, layoutTitleNumber, tabItems } =
        useAgreementForm({ agreementItemData: agreementItemData?.data, projectData });

    const contractChangeStatus = useMemo(() => agreementItemData?.data?.status, [agreementItemData?.data?.status]);

    const memoizedRequestIds = useMemo(() => ['agreementItem', 'agreementMtr', 'agreementUer', 'agreementRatio'], []);

    const [resetPageData] = useResetMultipleStoreData({
        requestIds: memoizedRequestIds,
    });

    useEffect(
        () => () => {
            resetPageData();
        },
        [resetPageData],
    );

    const disabledByContractChangeStatus = useMemo(
        () => contractChangeStatus !== ContractChangeStatusEnum.Formation,
        [contractChangeStatus],
    );

    const panelInfoText = useMemo(() => {
        if (contractChangeStatus === ContractChangeStatusEnum.Approved) {
            return {
                default: 'Для записей, включенных в Дополнительное соглашение, присвоен статус записи «Условие ДС»',
            };
        }

        if (contractChangeStatus === ContractChangeStatusEnum.Formation) {
            return {
                other: 'Возможно добавить записи прочих затрат со статусом «Новая запись», а также записи со статусом «Условие договора», но типом связи – «Новая связь»',
                default:
                    'Возможно добавить записи со статусом «Новая запись», а также с записи со статусом «Условие договора», но типом связи – «Новая связь»',
                mtr: 'Возможно добавить записи со статусом «Новая запись»',
            };
        }

        return undefined;
    }, [contractChangeStatus]);

    const hasItemRow = agreementItemData?.data?.[currentTab.counterId] !== 0;

    const [contractSeasons] = useUerContractSeasons(contractId);

    return (
        <ProjectPageLayout
            statusContractChange={contractChangeStatus}
            title={`Дополнительное соглашение ${layoutTitleNumber}`}
            breadCrumbsItems={breadCrumbsItems}
        >
            <AgreementFormToolbar
                currentContractId={contractId}
                disabledByStatus={disabledByContractChangeStatus}
                currentAgreementId={agreementId}
                refreshProject={refreshProject}
                needUpdateCurrentPriceList={needUpdateCurrentPriceList}
                needUpdatePriceListHandler={needUpdatePriceListHandler}
                isDisabledByProjectStatus={isDisabledByProjectStatus}
                isDisabledByContractStatus={isDisabledByContractStatus}
                currentAgreementStatus={agreementItemData?.data?.status}
                rulesAccess={additionalAgreementRules}
                agreementTableApiRef={agreementTableApiRef}
            />

            <Tabs
                value={currentTab}
                onChange={onChangeTab}
                items={tabItems}
                getItemLabel={getTabLabel}
                renderItem={renderTabItem}
                style={TabsStyle}
            />
            {currentTab.id === 1 && (
                <AgreementPriceListUer
                    disabledByStatus={disabledByContractChangeStatus}
                    agreementId={agreementId}
                    refreshData={refreshData}
                    infoText={panelInfoText?.default}
                    needUpdateCurrentPriceList={needUpdateCurrentPriceList}
                    needUpdatePriceListHandler={needUpdatePriceListHandler}
                    isDisabledByProjectStatus={isDisabledByProjectStatus}
                    isDisabledByContractStatus={isDisabledByContractStatus}
                    contractChangeStatus={contractChangeStatus}
                    rulesAccess={additionalAgreementRules}
                    agreementTableApiRef={agreementTableApiRef}
                    hasItemRow={hasItemRow}
                />
            )}
            {currentTab.id === 2 && (
                <AgreementPriceListMtr
                    disabledByStatus={disabledByContractChangeStatus}
                    agreementId={agreementId}
                    refreshData={refreshData}
                    infoText={panelInfoText?.mtr ?? panelInfoText?.default}
                    needUpdateCurrentPriceList={needUpdateCurrentPriceList}
                    needUpdatePriceListHandler={needUpdatePriceListHandler}
                    isDisabledByProjectStatus={isDisabledByProjectStatus}
                    isDisabledByContractStatus={isDisabledByContractStatus}
                    contractChangeStatus={contractChangeStatus}
                    agreementTableApiRef={agreementTableApiRef}
                    hasItemRow={hasItemRow}
                />
            )}
            {currentTab.id === 3 && (
                <AgreementPriceListRatio
                    disabledByStatus={disabledByContractChangeStatus}
                    agreementId={agreementId}
                    refreshData={refreshData}
                    infoText={panelInfoText?.default}
                    needUpdateCurrentPriceList={needUpdateCurrentPriceList}
                    needUpdatePriceListHandler={needUpdatePriceListHandler}
                    isDisabledByProjectStatus={isDisabledByProjectStatus}
                    isDisabledByContractStatus={isDisabledByContractStatus}
                    contractChangeStatus={contractChangeStatus}
                    agreementTableApiRef={agreementTableApiRef}
                    hasItemRow={hasItemRow}
                />
            )}
            {currentTab.id === 4 && (
                <AgreementPriceListOther
                    disabledByStatus={disabledByContractChangeStatus}
                    agreementId={agreementId}
                    refreshData={refreshData}
                    infoText={panelInfoText?.other}
                    needUpdateCurrentPriceList={needUpdateCurrentPriceList}
                    needUpdatePriceListHandler={needUpdatePriceListHandler}
                    isDisabledByProjectStatus={isDisabledByProjectStatus}
                    isDisabledByContractStatus={isDisabledByContractStatus}
                    contractChangeStatus={contractChangeStatus}
                    agreementTableApiRef={agreementTableApiRef}
                    hasItemRow={hasItemRow}
                    contractId={contractId}
                    contractSeasons={contractSeasons}
                />
            )}
        </ProjectPageLayout>
    );
};

AgreementForm.displayName = 'AgreementForm';
